/**
 * FlowSpec ↔ Canvas Stores Converters
 *
 * Bidirectional conversion between flowSpec (backend format) and Canvas stores (new architecture).
 * Handles all component types: regular, choice, parallel, compound, and disconnected components.
 *
 * This adapter enables the Strangler Fig migration pattern by maintaining compatibility
 * between the old flowSpec model and the new Canvas-first architecture.
 */
/* eslint-disable @typescript-eslint/no-use-before-define, no-plusplus, no-underscore-dangle, @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, no-useless-return, no-restricted-syntax, no-continue, no-console, @typescript-eslint/no-explicit-any */

import { ComponentSpec, FlowSpec } from '@digibee/flow';

import { CanvasNode, CanvasEdge, CanvasPosition } from '../canvasStore';
import {
  ComponentConfig,
  RegularComponentConfig,
  ChoiceComponentConfig,
  ParallelComponentConfig,
  CompoundComponentConfig,
  ChoiceCondition,
  ParallelExecution
} from '../componentConfigStore';
import { Track, TrackType } from '../trackStore';

import { JSON_GENERATOR_CONNECTOR } from '~/scenes/Build/constants';
import { formatCanvasNodesPositions } from '~/scenes/Build/scenes/FlowBuilder/components/FlowCanvas/utils/formatFlow';

// ============================================================================
// Types
// ============================================================================

interface ConversionContext {
  nodePositions: Map<string, CanvasPosition>; // Track node positions during conversion
  trackIndex: number; // Current track being processed
  levelOffset: { x: number; y: number }; // Offset for nested levels
}

// ============================================================================
// FlowSpec → Canvas Stores
// ============================================================================

/**
 * Resolve parentComponentId for Choice branch tracks.
 * Must run after all tracks are created, since branch tracks
 * may be processed before the Choice component during conversion.
 */
function resolveChoiceTrackParents(
  configurations: Map<string, ComponentConfig>,
  tracks: Map<string, Track>
): void {
  configurations.forEach((config, configId) => {
    if (config.type !== 'choice') return;

    const choiceConfig = config as ChoiceComponentConfig;
    choiceConfig.when.forEach(condition => {
      const track = tracks.get(condition.target);
      if (track && !track.parentComponentId) {
        track.parentComponentId = configId;
      }
    });

    if (choiceConfig.otherwise) {
      const otherwiseTrack = tracks.get(choiceConfig.otherwise);
      if (otherwiseTrack && !otherwiseTrack.parentComponentId) {
        otherwiseTrack.parentComponentId = configId;
      }
    }
  });
}

function flowSpecTrackHasSteps(flowSpec: FlowSpec, trackKey: string): boolean {
  const steps = flowSpec[trackKey];
  return Array.isArray(steps) && steps.length > 0;
}

function isBlankCompoundTrackRef(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

/**
 * Repair compound connectors whose onProcess/onException were cleared while
 * canonical tracks still exist in flowSpec (legacy migration) — avoids Camel
 * `direct:` without a route name at runtime.
 */
function healCompoundConnectorParamsFromFlowSpec(
  flowSpec: FlowSpec,
  configurations: Map<string, ComponentConfig>
): void {
  configurations.forEach((config, componentId) => {
    if (!('params' in config) || !config.params) return;
    const params = config.params as Record<string, unknown>;
    if (!('onProcess' in params) || !('onException' in params)) return;

    const compound = config as CompoundComponentConfig;
    const canonicalOnProcess = `${componentId}-onProcessTrack`;
    const canonicalOnException = `${componentId}-onExceptionTrack`;

    let { onProcess, onException } = compound.params;

    if (
      isBlankCompoundTrackRef(onProcess) &&
      flowSpecTrackHasSteps(flowSpec, canonicalOnProcess)
    ) {
      onProcess = canonicalOnProcess;
    }
    if (
      isBlankCompoundTrackRef(onException) &&
      flowSpecTrackHasSteps(flowSpec, canonicalOnException)
    ) {
      onException = canonicalOnException;
    }

    if (
      onProcess === compound.params.onProcess &&
      onException === compound.params.onException
    ) {
      return;
    }

    configurations.set(componentId, {
      ...compound,
      params: {
        ...compound.params,
        onProcess,
        onException
      }
    });
  });
}

/**
 * The flowSpec leaves out tracks without steps, so the empty onProcess / onException of a
 * compound connector is not loaded. Create it empty, as adding the connector does, so the
 * canvas can open it and build its breadcrumb.
 */
function addMissingCompoundTracks(
  configurations: Map<string, ComponentConfig>,
  tracks: Map<string, Track>
): void {
  configurations.forEach((config, componentId) => {
    if (!('params' in config) || !config.params) return;
    const params = config.params as Record<string, unknown>;
    if (!('onProcess' in params) || !('onException' in params)) return;

    (['onProcess', 'onException'] as const).forEach(type => {
      const trackName = params[type];
      if (isBlankCompoundTrackRef(trackName)) return;
      const name = trackName as string;
      if (tracks.has(name)) return;

      tracks.set(name, {
        name,
        type,
        parentComponentId: componentId,
        componentIds: [],
        isDisconnected: false
      });
    });
  });
}

function syncCompoundNodeTagsFromConfigs(
  nodes: Map<string, CanvasNode>,
  configurations: Map<string, ComponentConfig>
): void {
  configurations.forEach((config, id) => {
    if (!('params' in config) || !config.params) return;
    const params = config.params as Record<string, unknown>;
    if (!('onProcess' in params) || !('onException' in params)) return;
    if (
      isBlankCompoundTrackRef(params.onProcess) &&
      isBlankCompoundTrackRef(params.onException)
    ) {
      return;
    }
    const node = nodes.get(id);
    if (!node || node.tags.includes('compound')) return;
    nodes.set(id, {
      ...node,
      tags: [...node.tags, 'compound', 'subpipeline']
    });
  });
}

type ChoiceWhenCondition = {
  target: string;
  simple?: string;
  jsonPath?: string;
  doubleBraces?: string;
  __documentation__?: string;
  toolData?: Record<string, unknown>;
};

const isChoiceSpec = (
  spec: ComponentSpec
): spec is ComponentSpec & {
  when?: ChoiceWhenCondition[];
  otherwise?: string;
} => spec.type === 'choice' || spec.type === 'tool-router';

/**
 * Convert a FlowSpec to Canvas stores representation
 *
 * @param flowSpec - The backend flowSpec structure
 * @param options - Conversion options
 * @param options.preservePositions - Whether to preserve existing positions (default: true)
 * @param options.existingPositions - Map of existing node positions to preserve
 * @returns Canvas stores data (nodes, edges, configs, tracks)
 */
export function flowSpecToCanvasStores(
  flowSpec: FlowSpec,
  options?: {
    preservePositions?: boolean;
    existingPositions?: Map<string, CanvasPosition>;
  }
): {
  nodes: Map<string, CanvasNode>;
  edges: Map<string, CanvasEdge>;
  configurations: Map<string, ComponentConfig>;
  tracks: Map<string, Track>;
} {
  const { preservePositions = true, existingPositions = new Map() } =
    options || {};

  const nodes = new Map<string, CanvasNode>();
  const edges = new Map<string, CanvasEdge>();
  const configurations = new Map<string, ComponentConfig>();
  const tracks = new Map<string, Track>();

  const context: ConversionContext = {
    nodePositions: preservePositions ? existingPositions : new Map(),
    trackIndex: 0,
    levelOffset: { x: 0, y: 0 }
  };

  // Add trigger node (preserve position if exists)
  const triggerPosition = existingPositions.get('trigger') || { x: 0, y: 0 };
  nodes.set('trigger', {
    id: 'trigger',
    position: triggerPosition,
    tags: ['trigger'],
    deletable: false
  });

  // Process all tracks in the flowSpec
  Object.keys(flowSpec).forEach(trackName => {
    const trackComponents = flowSpec[trackName];
    if (!trackComponents || !Array.isArray(trackComponents)) {
      return;
    }

    // Determine track type and parent
    const trackType = determineTrackType(trackName, flowSpec);
    const parentId = extractParentIdFromTrackName(trackName, flowSpec);

    // Create track entry
    const componentIds: string[] = [];

    // Process each component in the track
    trackComponents.forEach((componentSpec, index) => {
      componentIds.push(componentSpec.id);

      // Convert component to canvas node
      const nodePosition = context.nodePositions.get(componentSpec.id) || {
        x: 0,
        y: 0
      };
      const node = componentSpecToCanvasNode(componentSpec, trackName);
      nodes.set(node.id, { ...node, position: nodePosition });

      // Convert component to configuration
      const config = componentSpecToComponentConfig(componentSpec);
      configurations.set(componentSpec.id, config);

      // Create edge from previous component
      if (index === 0 && trackName === 'start') {
        // Connect to trigger
        const edge: CanvasEdge = {
          id: `trigger-${componentSpec.id}`,
          sourceId: 'trigger',
          targetId: componentSpec.id,
          tags: ['default'],
          deletable: true
        };
        edges.set(edge.id, edge);
      } else if (index > 0) {
        // Connect to previous component in track
        const prevComponent = trackComponents[index - 1];
        const edge: CanvasEdge = {
          id: `${prevComponent.id}-${componentSpec.id}`,
          sourceId: prevComponent.id,
          targetId: componentSpec.id,
          tags: ['default'],
          deletable: true
        };
        edges.set(edge.id, edge);
      }

      // Handle special component types
      handleChoiceComponent(componentSpec, edges, flowSpec);
      handleParallelComponent(componentSpec, edges, flowSpec);
      handleCompoundComponent(componentSpec, edges, flowSpec);
    });

    // Increment trackIndex AFTER processing all nodes in a disconnected track
    // This ensures all nodes in the same track have the same Y position
    if (trackName.startsWith('disconnected-')) {
      context.trackIndex++;
    }

    // Add track to tracks map
    tracks.set(trackName, {
      name: trackName,
      type: trackType as TrackType,
      parentComponentId: parentId,
      componentIds,
      isDisconnected: trackType === 'disconnected'
    });
  });

  healCompoundConnectorParamsFromFlowSpec(flowSpec, configurations);

  addMissingCompoundTracks(configurations, tracks);

  syncCompoundNodeTagsFromConfigs(nodes, configurations);

  resolveChoiceTrackParents(configurations, tracks);

  let finalNodes = nodes;

  // Multiple empty positions overlap in the canvas, so we must format
  const hasMultipleEmptyPositions =
    Array.from(nodes.values()).filter(
      ({ position }) => position.x === 0 && position.y === 0
    ).length >= 2;

  // Convert flowSpec to Canvas stores
  const shouldPreservePositions =
    preservePositions && !hasMultipleEmptyPositions;

  if (!shouldPreservePositions) {
    const formattedNodes = formatCanvasNodesPositions({
      nodes,
      edges,
      currentLevelName: 'start',
      tracks
    });
    finalNodes = formattedNodes.nodes;
  }

  return { nodes: finalNodes, edges, configurations, tracks };
}

/**
 * Convert a ComponentSpec to a CanvasNode
 */
function componentSpecToCanvasNode(
  spec: ComponentSpec,
  trackName: string
): CanvasNode {
  // Determine tags based on component type
  const tags: string[] = ['component'];
  if (spec.type === 'choice' || spec.type === 'tool-router') {
    tags.push('choice');
  } else if (spec.name === 'parallel-execution-connector') {
    tags.push('parallel-execution-connector');
  } else if (spec.params?.onProcess || spec.params?.onException) {
    tags.push('compound', 'subpipeline');
  }
  if (spec.type === 'connector') {
    tags.push(spec.name);
  }

  const isSubLevelTrack =
    trackName.endsWith('-onProcessTrack') ||
    trackName.endsWith('-onExceptionTrack');

  return {
    id: spec.id,
    position: { x: 0, y: 0 },
    tags,
    deletable:
      isSubLevelTrack ||
      (!spec.id.startsWith('api-first:') && !spec.id.startsWith('mcp-server:'))
  };
}

/** Strip trailing ` (Mock)` from json-generator steps (mock exec may persist mock name in stepName). */
function normalizeJsonGeneratorStepName(
  spec: ComponentSpec,
  stepName: string
): string {
  if (spec.name?.toLowerCase() !== JSON_GENERATOR_CONNECTOR) {
    return stepName;
  }
  return stepName.replace(/\s*\(Mock\)\s*$/i, '').trimEnd();
}

/**
 * Convert a ComponentSpec to a ComponentConfig
 */
type ChoiceSpecExtended = ComponentSpec & {
  when?: ChoiceWhenCondition[];
  otherwise?: string;
  __documentation__?: string;
  __otherwiseDocumentation__?: string;
  externalJWT?: boolean;
  externalToken?: Record<string, unknown>;
  accountLabels?: Record<string, unknown>;
  jwsAlgorithm?: string;
};

function componentSpecToComponentConfig(spec: ComponentSpec): ComponentConfig {
  const { params, ...otherSpecFields } = spec as ComponentSpec & {
    params?: Record<string, unknown>;
  };

  if (
    spec.name === 'for-each-connector' &&
    params?.jsonPath &&
    !params.expression
  ) {
    params.expression = params.jsonPath;
    delete params.jsonPath;
  }

  // Choice component (includes tool-router)
  if (isChoiceSpec(spec)) {
    const choiceSpec = spec as ChoiceSpecExtended;
    const conditions: ChoiceCondition[] =
      choiceSpec.when?.map((when: ChoiceWhenCondition) => ({
        target: when.target,
        type: 'when' as const,
        simple: when.simple,
        jsonPath: when.jsonPath,
        doubleBraces: when.doubleBraces,
        /* eslint-disable-next-line no-underscore-dangle */
        __documentation__: when.__documentation__,
        ...(when.toolData && { toolData: when.toolData })
      })) || [];

    const config = {
      id: spec.id,
      type: spec.type,
      name: spec.name || '',
      stepName: spec.stepName || '',
      params: {},
      /* eslint-disable-next-line no-underscore-dangle */
      __documentation__: choiceSpec.__documentation__,
      when: conditions,
      otherwise: choiceSpec.otherwise,
      /* eslint-disable-next-line no-underscore-dangle */
      __otherwiseDocumentation__: choiceSpec.__otherwiseDocumentation__,
      externalJWT: choiceSpec.externalJWT,
      externalToken: choiceSpec.externalToken,
      ...(choiceSpec.accountLabels && {
        accountLabels: choiceSpec.accountLabels
      }),
      ...(choiceSpec.jwsAlgorithm && { jwsAlgorithm: choiceSpec.jwsAlgorithm })
    };
    return config as ChoiceComponentConfig;
  }

  // Parallel component
  if (spec.name === 'parallel-execution-connector') {
    const executions: ParallelExecution[] =
      params?.parallelExecutions?.map((exec: any) => ({
        target: exec.target,
        description: exec.description,
        executionId: exec.executionId,
        __documentation__: exec.__documentation__
      })) || [];

    return {
      id: spec.id,
      type: spec.type,
      name: spec.name || '',
      stepName: spec.stepName || '',
      params: {
        ...params,
        parallelExecutions: executions
      },
      __documentation__: (spec as any).__documentation__,
      doubleBracesAlias: (spec as any).doubleBracesAlias
    } as ParallelComponentConfig;
  }

  // Compound component (has onProcess/onException)
  if (params?.onProcess || params?.onException) {
    return {
      ...otherSpecFields,
      id: spec.id,
      type: spec.type,
      name: spec.name || '',
      stepName: spec.stepName || '',
      params: {
        ...params,
        onProcess: params.onProcess || '',
        onException: params.onException || ''
      },
      __documentation__: (spec as any).__documentation__,
      doubleBracesAlias: (spec as any).doubleBracesAlias
    } as CompoundComponentConfig;
  }

  // Regular component

  return {
    ...otherSpecFields,
    id: spec.id,
    type: spec.type,
    name: spec.name || '',
    stepName: normalizeJsonGeneratorStepName(spec, spec.stepName || ''),
    params: params || {}
  } as RegularComponentConfig;
}

/**
 * Handle choice component edges
 */
function handleChoiceComponent(
  spec: ComponentSpec,
  edges: Map<string, CanvasEdge>,
  flowSpec: FlowSpec
): void {
  if (!isChoiceSpec(spec)) return;

  // Add edges for each when condition
  spec.when?.forEach((when: any) => {
    const targetTrack = flowSpec[when.target];
    if (targetTrack && targetTrack.length > 0) {
      const targetComponent = targetTrack[0];
      const edge: CanvasEdge = {
        id: `${spec.id}-${targetComponent.id}`,
        sourceId: spec.id,
        targetId: targetComponent.id,
        tags: ['choice', 'when'],
        deletable: true,
        metadata: {
          conditionType: 'when',
          label: when.target
        }
      };
      edges.set(edge.id, edge);
    }
  });

  // Add edge for otherwise condition
  if (spec.otherwise) {
    const otherwiseTrack = flowSpec[spec.otherwise];
    if (otherwiseTrack && otherwiseTrack.length > 0) {
      const otherwiseComponent = otherwiseTrack[0];
      const edge: CanvasEdge = {
        id: `${spec.id}-${otherwiseComponent.id}`,
        sourceId: spec.id,
        targetId: otherwiseComponent.id,
        tags: ['choice', 'otherwise'],
        deletable: true,
        metadata: {
          conditionType: 'otherwise',
          label: spec.otherwise
        }
      };
      edges.set(edge.id, edge);
    }
  }
}

/**
 * Handle parallel component edges
 */
function handleParallelComponent(
  spec: ComponentSpec,
  edges: Map<string, CanvasEdge>,
  flowSpec: FlowSpec
): void {
  if (spec.name !== 'parallel-execution-connector') return;

  const executions = spec.params?.parallelExecutions || [];
  executions.forEach((execution: any) => {
    const targetTrack = flowSpec[execution.target];
    if (targetTrack && targetTrack.length > 0) {
      const targetComponent = targetTrack[0];
      const edge: CanvasEdge = {
        id: `${spec.id}-${targetComponent.id}`,
        sourceId: spec.id,
        targetId: targetComponent.id,
        tags: ['parallel-execution'],
        deletable: true,
        metadata: {
          label: execution.description,
          description: execution.description,
          executionId: execution.executionId
        }
      };
      edges.set(edge.id, edge);
    }
  });
}

/**
 * Handle compound component (nested onProcess/onException)
 */
function handleCompoundComponent(
  spec: ComponentSpec,
  edges: Map<string, CanvasEdge>,
  flowSpec: FlowSpec
): void {
  if (!spec.params?.onProcess && !spec.params?.onException) return;

  // Note: Nested components don't have direct edges on the canvas
  // They are represented as separate levels/tracks
  // The connection is maintained through the compound component's params
}

/**
 * Determine track type based on track name and flowSpec
 */
function determineTrackType(trackName: string, flowSpec: FlowSpec): string {
  if (trackName === 'start') return 'start';
  if (trackName.startsWith('disconnected-')) return 'disconnected';
  if (trackName.endsWith('-onProcessTrack')) return 'onProcess';
  if (trackName.endsWith('-onExceptionTrack')) return 'onException';

  for (const [currentTrackName, components] of Object.entries(flowSpec)) {
    if (!components || !Array.isArray(components)) continue;

    for (const component of components) {
      if (isChoiceSpec(component)) {
        const choiceComponent = component as any;

        if (choiceComponent.when) {
          for (const condition of choiceComponent.when) {
            if (condition.target === trackName) {
              return 'choice-when';
            }
          }
        }

        if (choiceComponent.otherwise === trackName) {
          return 'choice-otherwise';
        }
      }

      if (
        component.name === 'parallel-execution-connector' &&
        component.params?.parallelExecutions
      ) {
        for (const execution of component.params.parallelExecutions) {
          if (execution.target === trackName) {
            return 'parallel-execution';
          }
        }
      }
    }
  }

  return '';
}

/**
 * Extract parent component ID from track name
 */
function extractParentIdFromTrackName(
  trackName: string,
  flowSpec: FlowSpec
): string | undefined {
  if (trackName === 'start') return undefined;

  // For disconnected tracks: disconnected-{levelName}:{componentId}
  if (trackName.startsWith('disconnected-')) {
    const match = trackName.match(/^disconnected-(.+?):/);
    if (match) {
      const levelName = match[1];
      if (levelName === 'start') {
        return undefined;
      }
      // For nested levels, extract parent from level name
      // Pattern: {componentId}-onProcessTrack or {componentId}-onExceptionTrack
      if (
        levelName.endsWith('-onProcessTrack') ||
        levelName.endsWith('-onExceptionTrack')
      ) {
        return levelName.replace(/-onProcessTrack|-onExceptionTrack/, '');
      }
    }
    return undefined;
  }

  // For onProcess/onException tracks
  if (
    trackName.endsWith('-onProcessTrack') ||
    trackName.endsWith('-onExceptionTrack')
  ) {
    return trackName.replace(/-onProcessTrack|-onExceptionTrack/, '');
  }

  for (const [currentTrackName, components] of Object.entries(flowSpec)) {
    if (!components || !Array.isArray(components)) continue;

    for (const component of components) {
      if (isChoiceSpec(component)) {
        const choiceComponent = component as any;

        if (choiceComponent.when) {
          for (const condition of choiceComponent.when) {
            if (condition.target === trackName) {
              return component.id;
            }
          }
        }

        if (choiceComponent.otherwise === trackName) {
          return component.id;
        }
      }

      if (
        component.name === 'parallel-execution-connector' &&
        component.params?.parallelExecutions
      ) {
        for (const execution of component.params.parallelExecutions) {
          if (execution.target === trackName) {
            return component.id;
          }
        }
      }
    }
  }

  return undefined;
}

// ============================================================================
// Canvas Stores → FlowSpec
// ============================================================================

/**
 * Convert Canvas stores to FlowSpec format
 *
 * @param nodes - Canvas nodes map
 * @param edges - Canvas edges map
 * @param configurations - Component configurations map
 * @param tracks - Tracks map
 * @returns FlowSpec object
 */
export function canvasStoresToFlowSpec(
  edges: Map<string, CanvasEdge>,
  configurations: Map<string, ComponentConfig>,
  tracks: Map<string, Track>
): FlowSpec {
  const flowSpec: FlowSpec = {};

  // Input validation
  if (!tracks || tracks.size === 0) {
    console.warn(
      '[canvasStoresToFlowSpec] No tracks provided, returning empty FlowSpec'
    );
    return flowSpec;
  }

  // Build edges lookup for efficient access
  const edgesBySource = new Map<string, CanvasEdge[]>();
  const edgesByTarget = new Map<string, CanvasEdge[]>();

  edges.forEach(edge => {
    // Group edges by source
    const sourceEdges = edgesBySource.get(edge.sourceId) || [];
    sourceEdges.push(edge);
    edgesBySource.set(edge.sourceId, sourceEdges);

    // Group edges by target
    const targetEdges = edgesByTarget.get(edge.targetId) || [];
    targetEdges.push(edge);
    edgesByTarget.set(edge.targetId, targetEdges);
  });

  // Process each track
  tracks.forEach(track => {
    // Skip tracks with no components
    if (!track.componentIds || track.componentIds.length === 0) {
      return;
    }

    const components: ComponentSpec[] = [];

    // Build components array from componentIds in track
    track.componentIds.forEach(componentId => {
      const config = configurations.get(componentId);

      // Skip components without configuration (with warning)
      if (!config) {
        console.warn(
          `[canvasStoresToFlowSpec] Component ${componentId} in track ${track.name} has no configuration`
        );
        return;
      }

      const componentSpec = componentConfigToComponentSpec(
        config,
        tracks,
        edgesBySource
      );
      components.push(componentSpec);
    });

    // Add track to flowSpec only if it has components
    if (components.length > 0) {
      flowSpec[track.name] = components;
    }
  });

  return flowSpec;
}

/**
 * Convert ComponentConfig to ComponentSpec
 */
export function componentConfigToComponentSpec(
  config: ComponentConfig,
  tracks: Map<string, Track>,
  edgesBySource: Map<string, CanvasEdge[]>
): ComponentSpec {
  const baseSpec: any = {
    id: config.id,
    type: config.type,
    name: config.name,
    stepName: config.stepName
  };

  // Copy all additional fields from config (excluding known base fields and params)
  const knownBaseFields = new Set([
    'id',
    'type',
    'name',
    'stepName',
    'params',
    'when',
    'otherwise',
    '__otherwiseDocumentation__'
  ]);

  Object.keys(config).forEach(key => {
    if (
      !knownBaseFields.has(key) &&
      config[key as keyof typeof config] !== undefined
    ) {
      baseSpec[key] = config[key as keyof typeof config];
    }
  });

  // Choice component - rebuild when/otherwise from edges and tracks
  if ('when' in config) {
    const choiceConfig = config as ChoiceComponentConfig;
    const componentEdges = edgesBySource.get(config.id) || [];

    // Rebuild when conditions from edges and existing config
    const whenConditions: any[] = [];
    const processedTargets = new Set<string>();

    choiceConfig.when.forEach(condition => {
      // Find the track that matches this condition's target
      // Try first with parentComponentId, then without (more lenient)
      let targetTrack = Array.from(tracks.values()).find(
        track =>
          track.name === condition.target &&
          track.parentComponentId === config.id
      );

      // Fallback: search by name alone if not found with parentComponentId
      if (!targetTrack) {
        targetTrack = Array.from(tracks.values()).find(
          track => track.name === condition.target
        );
      }

      if (targetTrack) {
        whenConditions.push({
          target: condition.target,
          simple: condition.simple,
          jsonPath: condition.jsonPath,
          doubleBraces: condition.doubleBraces,
          __documentation__: condition.__documentation__,
          ...(condition.toolData && { toolData: condition.toolData })
        });
        processedTargets.add(condition.target);
      }
    });

    // Check edges for additional when conditions (in case config is incomplete)
    componentEdges.forEach(edge => {
      if (edge.tags?.includes('when')) {
        const targetTrack = Array.from(tracks.values()).find(
          track =>
            track.componentIds.includes(edge.targetId) &&
            !processedTargets.has(track.name)
        );

        if (targetTrack && !processedTargets.has(targetTrack.name)) {
          // Find matching condition in original config
          const existingCondition = choiceConfig.when.find(
            c => c.target === targetTrack.name
          );

          if (existingCondition) {
            whenConditions.push({
              target: targetTrack.name,
              simple: existingCondition.simple,
              jsonPath: existingCondition.jsonPath,
              doubleBraces: existingCondition.doubleBraces,
              __documentation__: existingCondition.__documentation__,
              ...(existingCondition.toolData && {
                toolData: existingCondition.toolData
              })
            });
            processedTargets.add(targetTrack.name);
          }
        }
      }
    });

    baseSpec.when = whenConditions;

    // Rebuild otherwise from edges/tracks
    if (choiceConfig.otherwise) {
      // Try with parentComponentId first, then without
      let otherwiseTrack = Array.from(tracks.values()).find(
        track =>
          track.name === choiceConfig.otherwise &&
          track.parentComponentId === config.id
      );

      // Fallback: search by name alone
      if (!otherwiseTrack) {
        otherwiseTrack = Array.from(tracks.values()).find(
          track => track.name === choiceConfig.otherwise
        );
      }

      if (otherwiseTrack) {
        baseSpec.otherwise = choiceConfig.otherwise;
        if (choiceConfig.__otherwiseDocumentation__) {
          baseSpec.__otherwiseDocumentation__ =
            choiceConfig.__otherwiseDocumentation__;
        }
      }
    }

    // Validation: log warning if choice has no branches
    if (whenConditions.length === 0 && !baseSpec.otherwise) {
      console.warn(
        `[canvasStoresToFlowSpec] Choice component ${config.id} has no when conditions or otherwise branch`
      );
    }

    return baseSpec as ComponentSpec;
  }

  // Parallel component - rebuild executions from edges and tracks
  if (
    'params' in config &&
    config.params &&
    'parallelExecutions' in config.params
  ) {
    const parallelConfig = config as ParallelComponentConfig;
    const componentEdges = edgesBySource.get(config.id) || [];

    // Rebuild parallel executions from edges and existing config
    const executions: any[] = [];
    const processedTargets = new Set<string>();

    parallelConfig.params.parallelExecutions.forEach(exec => {
      // Find the track that matches this execution's target
      // Try first with parentComponentId, then without (more lenient)
      let targetTrack = Array.from(tracks.values()).find(
        track =>
          track.name === exec.target && track.parentComponentId === config.id
      );

      // Fallback: search by name alone if not found with parentComponentId
      if (!targetTrack) {
        targetTrack = Array.from(tracks.values()).find(
          track => track.name === exec.target
        );
      }

      if (targetTrack) {
        executions.push({
          target: exec.target,
          description: exec.description,
          executionId: exec.executionId,
          __documentation__: exec.__documentation__
        });
        processedTargets.add(exec.target);
      }
    });

    // Check edges for additional executions (in case config is incomplete)
    componentEdges.forEach(edge => {
      if (edge.tags?.includes('parallel-execution')) {
        const targetTrack = Array.from(tracks.values()).find(
          track =>
            track.componentIds.includes(edge.targetId) &&
            !processedTargets.has(track.name)
        );

        if (targetTrack && !processedTargets.has(targetTrack.name)) {
          // Find matching execution in original config
          const existingExec = parallelConfig.params.parallelExecutions.find(
            e => e.target === targetTrack.name
          );

          if (existingExec) {
            executions.push({
              target: targetTrack.name,
              description: existingExec.description,
              executionId: existingExec.executionId,
              __documentation__: existingExec.__documentation__
            });
            processedTargets.add(targetTrack.name);
          }
        }
      }
    });

    // Validation: log warning if parallel has no executions
    if (executions.length === 0) {
      console.warn(
        `[canvasStoresToFlowSpec] Parallel component ${config.id} has no parallel executions`
      );
    }

    baseSpec.params = {
      ...parallelConfig.params,
      parallelExecutions: executions
    };
    return baseSpec as ComponentSpec;
  }

  // Compound component
  if (
    'params' in config &&
    config.params &&
    'onProcess' in config.params &&
    'onException' in config.params
  ) {
    const compoundConfig = config as CompoundComponentConfig;
    const canonicalOnProcess = `${compoundConfig.id}-onProcessTrack`;
    const canonicalOnException = `${compoundConfig.id}-onExceptionTrack`;

    let { onProcess } = compoundConfig.params;
    let { onException } = compoundConfig.params;

    if (isBlankCompoundTrackRef(onProcess)) {
      const onProcessTrack = tracks.get(canonicalOnProcess);
      if (onProcessTrack && onProcessTrack.componentIds.length > 0) {
        onProcess = canonicalOnProcess;
      }
    }
    if (isBlankCompoundTrackRef(onException)) {
      const onExceptionTrack = tracks.get(canonicalOnException);
      if (onExceptionTrack && onExceptionTrack.componentIds.length > 0) {
        onException = canonicalOnException;
      }
    }

    baseSpec.params = {
      ...compoundConfig.params,
      onProcess,
      onException
    };
    return baseSpec as ComponentSpec;
  }

  // Regular component
  if ('params' in config && config.params) {
    baseSpec.params = config.params;
  } else {
    // Ensure params is always present, even if empty
    baseSpec.params = {};
  }

  return baseSpec as ComponentSpec;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Rebuild tracks from edges and nodes
 * Useful when tracks might be out of sync with the actual edge connections
 */
export function rebuildTracksFromEdges(
  nodes: Map<string, CanvasNode>,
  edges: Map<string, CanvasEdge>,
  configurations: Map<string, ComponentConfig>
): Map<string, Track> {
  const tracks = new Map<string, Track>();
  const processedComponents = new Set<string>();

  // Build edge lookup
  const edgesBySource = new Map<string, CanvasEdge[]>();
  const edgesByTarget = new Map<string, CanvasEdge[]>();

  edges.forEach(edge => {
    const sourceEdges = edgesBySource.get(edge.sourceId) || [];
    sourceEdges.push(edge);
    edgesBySource.set(edge.sourceId, sourceEdges);

    const targetEdges = edgesByTarget.get(edge.targetId) || [];
    targetEdges.push(edge);
    edgesByTarget.set(edge.targetId, targetEdges);
  });

  // Find start of main track (connected to trigger)
  const triggerEdges = edgesBySource.get('trigger') || [];
  if (triggerEdges.length > 0) {
    const startComponentId = triggerEdges[0].targetId;
    const startTrack = buildTrackFromComponent(
      startComponentId,
      'start',
      'start',
      undefined,
      edgesBySource,
      edgesByTarget,
      configurations,
      processedComponents
    );
    tracks.set('start', startTrack);
  }

  // Find disconnected components (no incoming edges)
  nodes.forEach(node => {
    if (node.id === 'trigger') return;
    if (processedComponents.has(node.id)) return;

    const incomingEdges = edgesByTarget.get(node.id) || [];
    if (incomingEdges.length === 0) {
      const trackName = `disconnected-start:${node.id}`;

      const disconnectedTrack = buildTrackFromComponent(
        node.id,
        trackName,
        'disconnected',
        undefined,
        edgesBySource,
        edgesByTarget,
        configurations,
        processedComponents
      );
      tracks.set(trackName, disconnectedTrack);
    }
  });

  return tracks;
}

/**
 * Build a track starting from a component
 */
function buildTrackFromComponent(
  startComponentId: string,
  trackName: string,
  trackType: TrackType,
  parentComponentId: string | undefined,
  edgesBySource: Map<string, CanvasEdge[]>,
  edgesByTarget: Map<string, CanvasEdge[]>,
  configurations: Map<string, ComponentConfig>,
  processedComponents: Set<string>
): Track {
  const componentIds: string[] = [];
  let currentComponentId: string | null = startComponentId;

  // Follow the chain of edges to build the track
  while (currentComponentId && !processedComponents.has(currentComponentId)) {
    componentIds.push(currentComponentId);
    processedComponents.add(currentComponentId);

    // Find next component in track
    const outgoingEdges: CanvasEdge[] =
      edgesBySource.get(currentComponentId) || [];

    // Filter to regular connections (not choice/parallel branches)
    const regularEdges: CanvasEdge[] = outgoingEdges.filter(
      (edge: CanvasEdge) =>
        !edge.tags?.includes('choice') &&
        !edge.tags?.includes('parallel-execution')
    );

    if (regularEdges.length > 0) {
      const nextComponentId = regularEdges[0].targetId;

      // Stop if we hit a component that's already processed or creates a branch
      const incomingEdges: CanvasEdge[] =
        edgesByTarget.get(nextComponentId) || [];
      if (
        incomingEdges.length > 1 ||
        processedComponents.has(nextComponentId)
      ) {
        currentComponentId = null;
      } else {
        currentComponentId = nextComponentId;
      }
    } else {
      currentComponentId = null;
    }
  }

  return {
    name: trackName,
    type: trackType,
    parentComponentId,
    componentIds,
    isDisconnected: trackType === 'disconnected'
  };
}

/**
 * Validate that Canvas stores are internally consistent
 */
export function validateCanvasStores(
  nodes: Map<string, CanvasNode>,
  edges: Map<string, CanvasEdge>,
  configurations: Map<string, ComponentConfig>,
  tracks: Map<string, Track>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate nodes have corresponding configurations
  nodes.forEach(node => {
    if (node.id === 'trigger') return;

    if (!configurations.has(node.id)) {
      errors.push(`Node ${node.id} has no configuration`);
    }
  });

  // Validate configurations have corresponding nodes
  configurations.forEach(config => {
    if (!nodes.has(config.id)) {
      errors.push(`Configuration ${config.id} has no node`);
    }
  });

  // Validate edges reference existing nodes
  edges.forEach(edge => {
    if (edge.sourceId !== 'trigger' && !nodes.has(edge.sourceId)) {
      errors.push(
        `Edge ${edge.id} references non-existent source node ${edge.sourceId}`
      );
    }
    if (!nodes.has(edge.targetId)) {
      errors.push(
        `Edge ${edge.id} references non-existent target node ${edge.targetId}`
      );
    }
  });

  // Validate tracks reference existing components
  tracks.forEach(track => {
    track.componentIds.forEach(componentId => {
      if (!nodes.has(componentId)) {
        errors.push(
          `Track ${track.name} references non-existent component ${componentId}`
        );
      }
      if (!configurations.has(componentId)) {
        errors.push(
          `Track ${track.name} references component ${componentId} with no configuration`
        );
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors
  };
}
